import { ReactElement } from 'react'
import useUser from 'shared/hooks/use-user'
import { DomainAssetsInterface } from 'shared/types/domain'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import CommunityCart from 'modules/community/components/community-cart'
import { useCommunities } from 'modules/community/hooks/use-communities'
import { useLeaveCommunity } from 'modules/community/hooks/use-leave-community'
import { getCommunityPageLayout } from 'modules/community/layouts/get-community-page-layout'
import { CommunityInterface } from 'modules/community/types/community-interface'

const Communities = () => {
  const { loading } = useUser()
  const { data: communities = [], mutate } = useCommunities()
  const { leaveCommunity, isFetching } = useLeaveCommunity()

  const onLeaveCommunity = async (community: CommunityInterface) => {
    await leaveCommunity(community.path)
    await mutate(data => data?.filter(item => item.id !== community.id))
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {Array.from(Array(6).keys()).map(index => (
          <SkeletonCommunityItem key={index} />
        ))}
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {communities.map(community => (
          <CommunityCart
            key={`${community.name}-${community.id}`}
            community={community}
            isFetching={isFetching}
            onLeaveCommunity={onLeaveCommunity}
          />
        ))}
      </div>
    </>
  )
}

const SkeletonCommunityItem = () => {
  return (
    <div
      className="w-full max-w-[460px] animate-pulse overflow-hidden rounded-lg bg-background-secondary"
      key="skeleton-community-1"
    >
      <div className="mb-5 h-[180px] bg-skeleton-primary" />
      <div className="px-5 py-6">
        <div className="mb-5 h-4 w-36 rounded-sm bg-skeleton-primary" />
        {Array.from(Array(3).keys()).map(index => (
          <div className="mb-3 h-3 rounded-sm bg-skeleton-primary" key={index} />
        ))}
        <div className="float-right my-3 h-6 w-20 rounded-lg bg-skeleton-primary" />
      </div>
    </div>
  )
}

export const getServerSideProps = withDomainAssets(baseServerSideProps)

Communities.getLayout = (page: ReactElement, props: { domainAssets?: DomainAssetsInterface }) =>
  getCommunityPageLayout(page, {
    titleKey: 'communities.page_title',
    domainAssets: props.domainAssets,
  })

export default Communities
